import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Star, 
  User,
  Phone,
  Mail,
  CheckCircle,
  Loader,
  ArrowRight,
  MessageSquare,
  Bell,
  Shield,
  HelpCircle,
  AlertCircle,
  XCircle,
  Edit2,
  Ban,
  WifiOff
} from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

function Dashboard() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [upcomingBookings, setUpcomingBookings] = useState<any[]>([]);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [confirmCancelDialog, setConfirmCancelDialog] = useState<{
    isOpen: boolean;
    bookingId: string;
  } | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }

        setLoading(true);
        setError(null);

        // Fetch user profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;
        setUserProfile(profile);

        // Calculate profile completion
        let completion = 0;
        if (profile?.full_name) completion += 33.33;
        if (profile?.phone) completion += 33.33;
        if (user?.email) completion += 33.34;
        setProfileCompletion(Math.round(completion));

        // Fetch upcoming bookings using edge function
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No session found');

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-bookings`,
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const bookings = await response.json();
        
        // Filter for upcoming bookings (pending or confirmed)
        const upcoming = bookings
          .filter((booking: any) => ['pending', 'confirmed'].includes(booking.status))
          .sort((a: any, b: any) => new Date(a.booking_date).getTime() - new Date(b.booking_date).getTime())
          .slice(0, 3);
          
        setUpcomingBookings(upcoming);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
        showNotification('error', 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate, showNotification, user]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100 dark:bg-green-900/50 dark:text-green-400';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'cancelled':
        return 'text-red-600 bg-red-100 dark:bg-red-900/50 dark:text-red-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleEditBooking = (bookingId: string) => {
    navigate(`/book?edit=${bookingId}`);
  };

  const handleCancelBooking = async (bookingId: string) => {
    setCancellingBookingId(bookingId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-booking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ bookingId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to cancel booking');
      }

      setUpcomingBookings(bookings => 
        bookings.filter(booking => booking.id !== bookingId)
      );
      showNotification('success', 'Booking cancelled successfully');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      showNotification('error', error instanceof Error ? error.message : 'Failed to cancel booking');
    } finally {
      setCancellingBookingId(null);
      setConfirmCancelDialog(null);
    }
  };

  const openCancelConfirmation = (bookingId: string) => {
    setConfirmCancelDialog({
      isOpen: true,
      bookingId
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-center">
        <WifiOff className="w-12 h-12 text-gray-400" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Connection Error</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 py-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="text-2xl font-bold">
              Hello, {userProfile?.full_name || 'there'}!
            </h1>
            <p className="text-blue-100">Welcome to your dashboard</p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { icon: Calendar, label: 'Book', path: '/book' },
            { icon: Clock, label: 'History', path: '/history' },
            { icon: Star, label: 'Reviews', path: '/reviews' },
            { icon: User, label: 'Profile', path: '/profile' }
          ].map((action, index) => (
            <motion.button
              key={index}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(action.path)}
              className="app-card p-3 flex flex-col items-center gap-1"
            >
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <action.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{action.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Profile Completion */}
        {profileCompletion < 100 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="app-card p-4 mb-6"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-semibold dark:text-white">Complete Your Profile</h2>
              <span className="text-sm font-medium">
                {profileCompletion}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${profileCompletion}%` }}
              />
            </div>

            <button
              onClick={() => navigate('/profile')}
              className="w-full py-2.5 text-sm text-blue-600 dark:text-blue-400 font-medium border border-blue-600 dark:border-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Complete Profile
            </button>
          </motion.div>
        )}

        {/* Upcoming Bookings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold dark:text-white">Upcoming Services</h2>
            <button
              onClick={() => navigate('/history')}
              className="text-sm text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {upcomingBookings.length === 0 ? (
            <div className="app-card p-6 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No upcoming services scheduled
              </p>
              <button
                onClick={() => navigate('/book')}
                className="app-button py-2.5 px-6"
              >
                Book a Service
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <motion.div
                  key={booking.id}
                  whileTap={{ scale: 0.98 }}
                  className="app-card overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          booking.status
                        )}`}
                      >
                        {getStatusIcon(booking.status)}
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        ${(booking.services.price / 100).toFixed(2)}
                      </span>
                    </div>
                    
                    <h3 className="text-base font-semibold dark:text-white mb-2">
                      {booking.services.name}
                    </h3>

                    <div className="space-y-1 mb-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span>
                          {new Date(booking.booking_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span>
                          {new Date(booking.booking_date).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                          })}
                          <span className="text-gray-400 dark:text-gray-500 mx-1">·</span>
                          {booking.services.duration} min
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{booking.address}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleEditBooking(booking.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => openCancelConfirmation(booking.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <Ban className="w-3.5 h-3.5" />
                            Cancel
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => navigate(`/history?booking=${booking.id}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/20 rounded-lg transition-colors"
                      >
                        Details
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Help & Support */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <h2 className="text-lg font-semibold mb-4 dark:text-white">Help & Support</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/help')}
              className="app-card p-4 flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium dark:text-white">Help Center</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Find answers to common questions</p>
              </div>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/contact')}
              className="app-card p-4 flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium dark:text-white">Contact Support</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Get help from our team</p>
              </div>
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Confirmation Dialog */}
      {confirmCancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Cancel Booking
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmCancelDialog(null)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                No, keep it
              </button>
              <button
                onClick={() => handleCancelBooking(confirmCancelDialog.bookingId)}
                disabled={cancellingBookingId === confirmCancelDialog.bookingId}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {cancellingBookingId === confirmCancelDialog.bookingId ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <Ban className="w-4 h-4" />
                    Yes, cancel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;